import { Injectable, inject } from '@angular/core';
import { ApiBaseService } from '@shared/services/entities/api-base.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { WarehousesService } from '@shared/services/entities/warehouse.service';
import { ProductsService } from '@shared/services/entities/products.service';

/**
 * Servicios y catálogos de los módulos de logística:
 *
 *   /materialRequests  Solicitud de material (SOL-n)
 *   /transfers         Traspaso de material entre cuentas (TRA-n)
 *   /custody           Resguardo y devolución extraordinaria (RES-n / DEV-n)
 *
 * Las acciones que no son CRUD (enviar, recibir, correo a DHL, buscar pieza,
 * devolver) son POST a sub-rutas; el API responde `{ data: [resultado] }`.
 */

@Injectable({ providedIn: 'root' })
export class MaterialRequestsService extends ApiBaseService<any> {
  constructor() { super('materialRequests'); }
  private async action(path: string, body: any): Promise<any> {
    const r: any = await this.request<any>(`materialRequests/${path}`, 'POST', body);
    return r?.data?.[0] ?? null;
  }
  /** Dónde hay lo que falta: hospitales/lockers (stock) con su consumo y FSL (último corte SAP). */
  availability(id: string): Promise<any> { return this.action('availability', { id }); }
  /** Registra de dónde se surte (pasa a En surtido). */
  supply(id: string, plan: any[]): Promise<any> { return this.action('supply', { id, plan }); }
  /** Cambio de estado desde el menú: No aprobada, En surtido o Cancelada (Recibida es automática). */
  setStatus(id: string, status: string, note?: string | null): Promise<any> { return this.action('status', { id, status, note: note ?? null }); }
}

/** Roles que pueden surtir solicitudes (el API vuelve a validarlo). */
export const SUPPLY_ROLES = ['MESA DE CONTROL I', 'MESA DE CONTROL II', 'MESA DE CONTROL 1', 'MESA DE CONTROL 2',
  'ADMINISTRADOR EPIONE', 'ADMINISTRADOR MEDTRONIC'];
export const canSupplyRequests = (roles: string[] | undefined | null): boolean =>
  (roles ?? []).some(r => SUPPLY_ROLES.includes(r));

/** Solicitud de traspaso locker ↔ in-house del mismo hospital (SOT-n). */
@Injectable({ providedIn: 'root' })
export class TransferRequestsService extends ApiBaseService<any> {
  constructor() { super('transferRequests'); }
}

@Injectable({ providedIn: 'root' })
export class TransfersService extends ApiBaseService<any> {
  constructor() { super('transfers'); }
  private async action(path: string, body: any): Promise<any> {
    const r: any = await this.request<any>(`transfers/${path}`, 'POST', body);
    return r?.data?.[0] ?? null;
  }
  /** Enviar: mismo hospital = se recibe al momento; otro hospital = Por recolectar. */
  ship(id: string): Promise<any> { return this.action('ship', { id }); }
  /** Recibir en destino; `received` = ids de stock que sí llegaron (sin él, todas). */
  receive(id: string, received?: string[]): Promise<any> { return this.action('receive', { id, received }); }
  /** Correo de recolección a DHL (lo revisa y envía mesa de control). */
  dhlMail(body: { id: string; to: string[]; cc: string[]; subject: string; body: string; pdfBase64?: string; fileName?: string }): Promise<any> {
    return this.action('dhl-mail', body);
  }
}

@Injectable({ providedIn: 'root' })
export class CustodyService extends ApiBaseService<any> {
  constructor() { super('custody'); }
  private async action(path: string, body: any): Promise<any> {
    const r: any = await this.request<any>(`custody/${path}`, 'POST', body);
    return r?.data?.[0] ?? null;
  }
  /** ¿De dónde es esta etiqueta? / ¿existe este código en otras cuentas? */
  lookup(body: { place?: string | null; tag?: string; code?: string; batch?: string }): Promise<any> {
    return this.action('lookup', body);
  }
  /** Devolver resguardo / reincorporar devolución. */
  giveBack(id: string, warehouse?: string | null): Promise<any> { return this.action('return', { id, warehouse: warehouse || null }); }
}

// ── Catálogos compartidos ────────────────────────────────────────────────

const plain = (s: any) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toUpperCase();

export type SourceKind = 'FSL' | 'MACRO' | 'HOSPITAL';

/** Hospitales, lockers/in-house y productos, cargados una vez por pantalla. */
@Injectable({ providedIn: 'root' })
export class LogisticsCatalogs {
  private placesService = inject(PlacesService);
  private warehousesService = inject(WarehousesService);
  private productsService = inject(ProductsService);

  places: any[] = [];
  warehouses: any[] = [];
  products: any[] = [];
  placesById: Record<string, any> = {};
  warehousesById: Record<string, any> = {};
  productsById: Record<string, any> = {};
  private loaded: Promise<void> | null = null;

  load(force = false): Promise<void> {
    if (!this.loaded || force) {
      this.loaded = this.fetch().catch(err => { this.loaded = null; throw err; });
    }
    return this.loaded;
  }

  private async fetch(): Promise<void> {
    const cond = { searchType: 'default', condition: { deleted: false } };
    const [p, w, pr]: any[] = await Promise.all([
      this.placesService.search(cond), this.warehousesService.search(cond), this.productsService.search(cond),
    ]);
    this.places = (p?.data ?? []).filter((x: any) => x.active !== false);
    this.warehouses = (w?.data ?? []).filter((x: any) => x.active !== false);
    this.products = pr?.data ?? [];
    this.placesById = Object.fromEntries(this.places.map(x => [x.id, x]));
    this.warehousesById = Object.fromEntries(this.warehouses.map(x => [x.id, x]));
    this.productsById = Object.fromEntries(this.products.map(x => [x.id, x]));
  }

  placeName = (id: any): string => {
    const p = this.placesById[id];
    return p ? (p.alias || p.name || '') : '';
  };
  placeLabel = (p: any): string => (p ? (p.alias ? `${p.alias} — ${p.name ?? ''}` : (p.name ?? '')) : '');

  isInhouse = (w: any): boolean => plain(w?.storageType) === 'INHOUSE';
  warehouseLabel = (w: any): string => {
    if (!w) return '';
    return `${this.placeName(w.place)} · ${w.name ?? ''} (${this.isInhouse(w) ? 'In-house' : 'Locker'})`;
  };
  warehouseLabelById = (id: any): string => this.warehouseLabel(this.warehousesById[id]) || '—';

  productLabel = (p: any): string => (p ? `${p.code ?? ''} · ${p.description ?? ''}` : '');

  /** FSL (almacén local), Macrocentro u hospital, por el tipo del sitio o del locker. */
  sourceKind(w: any): SourceKind {
    const wt = plain(w?.type);
    const pt = plain(this.placesById[w?.place]?.type);
    if (wt === 'CEDIS_FSL' || wt === 'CEDIS_' + 'FLS' /* tipo viejo */ || pt === 'ALMACEN LOCAL' || pt === 'FSL') return 'FSL';
    if (wt === 'CEDIS_MACRO' || pt === 'MACROCENTRO') return 'MACRO';
    return 'HOSPITAL';
  }
  /** Dirección del sitio (campo "location"), para el correo a DHL. */
  placeAddress = (id: any): string => String(this.placesById[id]?.location ?? '').trim();
}

// ── Utilidades ───────────────────────────────────────────────────────────

export function fmtDate(value: any, withTime = true): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return withTime
    ? d.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    : d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Clase del chip de estado (comparten los tres módulos). */
export function statusChip(status: string): string {
  switch (status) {
    case 'Recibido': case 'Recibida': case 'Surtida': case 'Devuelto': case 'Cerrado': return 'chip chip--ok';
    case 'No aprobada': return 'chip chip--high';
    case 'Cancelado': case 'Cancelada': return 'chip chip--off';
    case 'Por recolectar': case 'En surtido': case 'Abierto': case 'Reportado': return 'chip chip--warn';
    case 'En tránsito': return 'chip chip--transit';
    default: return 'chip chip--open';
  }
}

export function currentUserId(): string {
  try { return sessionStorage.getItem('userId') ?? ''; } catch { return ''; }
}

/** Mensaje legible de un error del API. */
export function errMsg(err: any, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}
